import { useEffect, useState, useCallback } from "react";
import { streamClient } from "../../config/stream";
import { fetchChatToken, generateChannelId } from "./videoCallUtils";

/**
 * Custom hook for managing Stream Chat channel
 * Handles channel initialization, connection, and membership
 */
export const useChatChannel = ({
  classData,
  classId,
  activeRoomId,
  breakoutRooms,
  user,
}) => {
  const [chatChannel, setChatChannel] = useState(null);

  /**
   * Initializes and connects to Stream Chat
   */
  const connectToStreamChat = useCallback(async () => {
    if (!streamClient || !user?.uid) return;

    try {
      if (!streamClient.userID) {
        const token = await fetchChatToken(user.uid);
        await streamClient.connectUser(
          {
            id: user.uid,
            name: user.name || "User",
            image: user.photoUrl || "",
          },
          token,
        );
      }
    } catch (error) {
      console.error("[useChatChannel] Failed to connect to Stream Chat:", error);
      throw error;
    }
  }, [user]);

  /**
   * Initializes a chat channel for exam prep classes
   */
  const initializeExamPrepChannel = useCallback(
    async (channelId) => {
      try {
        const channel = streamClient.channel("messaging", channelId);
        await channel.watch();

        // Ensure user is a member
        try {
          await channel.addMembers([user.uid]);
        } catch (memberError) {
          console.log(
            "[useChatChannel] User may already be a member:",
            memberError.message,
          );
        }

        setChatChannel(channel);
        console.log(
          `[useChatChannel] Successfully initialized exam prep channel: ${channelId}`,
        );
        return channel;
      } catch (error) {
        console.error(
          `[useChatChannel] Error initializing exam prep channel:`,
          error,
        );
        throw error;
      }
    },
    [user],
  );

  /**
   * Initializes a regular chat channel
   */
  const initializeRegularChannel = useCallback(
    async (channelId, chatRoomName) => {
      try {
        const channel = streamClient.channel("messaging", channelId, {
          name: chatRoomName,
          members: [user.uid],
          created_by_id: user.uid,
        });

        await channel.watch();

        // Ensure user is a member (in case channel already existed)
        try {
          await channel.addMembers([user.uid]);
        } catch (memberError) {
          console.log(
            `[useChatChannel] User may already be a member:`,
            memberError.message,
          );
        }

        setChatChannel(channel);
        console.log(
          `[useChatChannel] Successfully initialized channel: ${channelId}`,
        );
        return channel;
      } catch (error) {
        console.error(
          "[useChatChannel] Error initializing chat channel:",
          error,
        );
        throw error;
      }
    },
    [user],
  );

  /**
   * Main channel initialization function
   */
  const initializeChannel = useCallback(async () => {
    if (!streamClient || !classData || !user?.uid) return;

    try {
      // Connect to Stream Chat first
      await connectToStreamChat();

      const isExamPrep = classData.classType === "exam_prep";
      const isIntroCall = classData.classType === "introductory_call";

      // Skip chat channel creation for introductory calls
      if (isIntroCall) {
        console.log(
          "[useChatChannel] Skipping chat channel creation for introductory call",
        );
        return;
      }

      // Handle exam prep classes
      if (isExamPrep) {
        try {
          const { getUserChannels } = await import("../../api/examPrepApi");
          const channels = await getUserChannels(user.uid);
          console.log("[useChatChannel] getUserChannels response:", channels);

          const classChannel = channels.find(
            (channel) =>
              channel.classId === classId ||
              channel.videoChatId === classId ||
              channel.id === classId,
          );

          if (classChannel?.channelId) {
            await initializeExamPrepChannel(classChannel.channelId);
            return;
          } else {
            console.warn(
              `[useChatChannel] No channelId found for exam prep class ${classId}`,
            );
          }
        } catch (apiError) {
          console.error(
            `[useChatChannel] Error fetching exam prep channel:`,
            apiError,
          );
        }
      }

      // Handle regular classes
      const channelId = generateChannelId(classId, activeRoomId);
      const isBreakoutRoom = activeRoomId && activeRoomId !== classId;
      const chatRoomName = isBreakoutRoom
        ? `${
            breakoutRooms.find((room) => room.id === activeRoomId)?.roomName ||
            "Breakout Room"
          } Call Chat`
        : `Class ${classId} Chat`;

      await initializeRegularChannel(channelId, chatRoomName);
    } catch (error) {
      console.error(
        "[useChatChannel] Error in channel initialization:",
        error,
      );
    }
  }, [
    classData,
    classId,
    activeRoomId,
    breakoutRooms,
    user,
    connectToStreamChat,
    initializeExamPrepChannel,
    initializeRegularChannel,
  ]);

  // Initialize channel when dependencies change
  useEffect(() => {
    initializeChannel().catch((err) => {
      console.error(
        "[useChatChannel] Unhandled error in channel initialization:",
        err,
      );
    });
  }, [initializeChannel]);

  return { chatChannel, setChatChannel };
};

